import {effect, Injector, runInInjectionContext, signal, WritableSignal} from '@angular/core';
import {MainInjector} from '@shared-types';

export type ConnectSignalOptions<T extends object, K extends keyof T> = {
	/**
	 * A function that will be called whenever the signal changes. Optional.
	 */
	updateFn?: (value: T[K]) => void;
	/**
	 * The injector to use for the effect. Optional.
	 */
	injector?: Injector;
};
/**
 * # connectSignal - Connect a signal to a property on an object
 * <p><code>connectSignal</code> is a utility function designed to simplify the process of connecting a signal to a property on an object. It takes an object, property name, and optional initial value as input and returns a signal that is connected to the property.</p>
 * <p>It also sets up a listener on the signal that updates the property whenever the signal changes.</p>
 * <br/><br/>
 * ```ts
 * connectSignal<T extends object = object, P extends keyof T = keyof T>(target: T, alias: P, initialValue?: T[P]): WritableSignal<T[P]>
 *  ```
 *  ## Parameters
 * @param target The object to connect the signal to
 * @param key
 * @param initialValue The initial value to set on the signal and the property. Optional.
 *
 * ## Returns WritableSignal
 * - A writable signal that is connected to the property on the object.
 *
 * ## Example
 * class Todo {
 * 	readonly title: string;
 * 	title$: WritableSignal<string>;
 *
 * 		constructor(title: string) {
 * 			this.title = title;
 * 			this.title$ = connectSignal(this, 'title', this.title); // all changes to title$ will be reflected in title and vice versa
 * 			}
 * }
 * @param opts
 */

export function connectSignal<T extends object, K extends keyof T>(target: T, key: K, initialValue?: T[K], opts?: Partial<ConnectSignalOptions<T, K>>): WritableSignal<T[K]> {
	let {injector} = opts || {};
	const {updateFn} = opts || {};
	injector = injector || MainInjector;

	const signal$ = signal<T[K]>(initialValue);

	runInInjectionContext(injector, () => {
		// @ts-ignore
		const originalToJson = target?.toJSON;
		// @ts-ignore
		target['toJSON'] = function () {
			const json = originalToJson?.call(this) || {};
			json[key] = signal$();
			return json;
		};

		// listen for changes to the signal and update the target property
		try {
			effect(
				() => {
					if (updateFn) {
						updateFn(signal$());
					} else {
						target[key] = signal$();
					}
				},
				{injector},
			);
		} catch (e) {
			/* empty */
		}

		// listen for target property changes and update the signal
		Object.defineProperty(target, key, {
			set(value: T[K]) {
				signal$.set(value);
			},
			get() {
				return signal$();
			},
		});
	});

	return signal$;
}
